import { lazy, useContext, useEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { Redirect, Route, useHistory } from 'react-router-dom'
import { AuthContext } from '../components/Auth/AuthProvider'
import CaiPopup from '../components/CaiPopup'
import { CaiChatProvider } from '../context/CaiChatContext'
import CategoryList from './Category/CategoryList'
import { Roles } from '../constants/common.constant'
import { MobileOnboarding } from 'src/components/CaiPopup/MobileOnboarding'
import { PlanWorkspaceWrapper } from '../components/common/PlanWorkspaceWrapper'
import { NotificationContext } from 'src/context/NotificationContext'
import PowerfulTour from 'src/components/common/PowerfulTour'
import PathwayForm from 'src/components/Goals/PathwayForm'
import UploadGoal from 'src/components/Goals/UploadGoal'

const Dashboard = lazy(() => import('./Dashboard'))
const ITDashboard = lazy(() => import('./IT/ITDashboard'))
const ProgramBuilder = lazy(() => import('./ProgramBuilder'))
const Programs = lazy(() => import('./Programs'))
const ProgressTrackers = lazy(() => import('./ProgressTrackers'))
const Tasks = lazy(() => import('./Tasks'))
const ProgressTracker = lazy(() => import('./ProgressTracker'))
const Presentation = lazy(() => import('./App/Presentation'))
const Help = lazy(() => import('./Dashboard/Help'))
const EmployeeProfile = lazy(() => import('./Employee/EmployeeProfile'))
const EmployeeTask = lazy(() => import('./Employee/EmployeeTask'))
const Library = lazy(() => import('./Library/index.tsx'))
const Teams = lazy(() => import('./Teams'))
const Header = lazy(() => import('../components/common/Header'))
const TaskEdit = lazy(() => import('./TaskEdit'))
const GoalStarterEdit = lazy(() => import('./GoalStarter/GoalStarterEdit'))
const EmployeeDashboard = lazy(() => import('./Employee/EmployeeDashboard'))
const PersonalHub = lazy(() => import('./PersonalHub'))
const PersonalGoals = lazy(() => import('./PersonalGoals'))
const PersonalSettings = lazy(() => import('./PersonalSettings'))
const Admin = lazy(() => import('./Admin'))
const StarterProgramList = lazy(
  () => import('./StarterProgram/StarterProgramList'),
)
const OrganizationList = lazy(() => import('./Organization/OrganizationList'))
const GoalStarter = lazy(() => import('./GoalStarter'))
const GoalLibraryCreate = lazy(() => import('../components/Goals/UploadGoal'))
const GoalLibrary = lazy(() => import('./GoalLibrary'))
const DiscoverableOrg = lazy(() => import('./DiscoverableOrgs'))
const ManageBilling = lazy(() => import('./ManageBilling'))
const PlanSelection = lazy(() => import('./PlanSelection'))
const PaymentSuccessful = lazy(
  () => import('./ManageBilling/PaymentSuccessful'),
)
const PaymentFailed = lazy(() => import('./ManageBilling/PaymentFailed'))
const PurchaseTokenPack = lazy(
  () => import('./ManageBilling/PurchaseTokenPack'),
)

const PaymentDetails = lazy(() => import('./ManageBilling/PaymentDetails'))
const CognentAIGuard = lazy(() => import('../components/Auth/CognentAIRoute'))

const GeneralRoutes = ({ roleName }) => {
  const routes = [
    { path: '/task/:taskId', component: Presentation },
    { path: '/task/:taskId/:taskInstanceId', component: Presentation },
    { path: '/task-duplicate/:taskId', component: TaskEdit },
    { path: '/task-edit/:taskId', component: TaskEdit },
    { path: '/help', component: Help },
  ]

  return (
    <>
      <Route exact path="/">
        <Redirect
          to={`${process.env.PUBLIC_URL}/${roleName.toLowerCase()}-dashboard`}
        />
      </Route>

      {routes.map((r, i) => (
        <Route
          key={i}
          exact
          component={r.component}
          path={`${process.env.PUBLIC_URL}${r.path}`}
        />
      ))}
    </>
  )
}

const HrRoutes = () => {
  const routes = [
    { path: '/hr-dashboard', component: Dashboard },
    { path: '/hr-programs', component: Programs },
    { path: '/hr-library', component: Library },
    { path: '/hr-tasks', component: Tasks },
    { path: '/hr-teams', component: Teams },
    { path: '/hr-program-builder', component: ProgramBuilder },
    { path: '/hr-program-builder/:programId', component: ProgramBuilder },
    { path: '/hr-progress-tracker', component: ProgressTrackers },
    { path: '/hr-progress-tracker/:programId', component: ProgressTracker },
  ]

  return routes.map((r, i) => (
    <Route
      key={i}
      exact
      component={r.component}
      path={`${process.env.PUBLIC_URL}${r.path}`}
    />
  ))
}

const withCognentAIGuard = (Component) => () => (
  <CognentAIGuard>
    <Component />
  </CognentAIGuard>
)

const ItRoutes = () => {
  const routes = [
    { path: '/it-dashboard', component: ITDashboard },
    { path: '/it-programs', component: Programs },
    { path: '/it-library', component: Library },
    { path: '/it-tasks', component: Tasks },
    { path: '/it-teams', component: Teams },
    { path: '/it-program-builder', component: ProgramBuilder },
    { path: '/it-program-builder/:programId', component: ProgramBuilder },
    { path: '/it-progress-tracker', component: ProgressTrackers },
    { path: '/it-progress-tracker/:programId', component: ProgressTracker },
    { path: '/it-categories', component: withCognentAIGuard(CategoryList) },
    { path: '/it-goal-starter', component: withCognentAIGuard(GoalStarter) },
    {
      path: '/it-goal-starter/edit/:id',
      component: withCognentAIGuard(GoalStarterEdit),
    },
    {
      path: '/it-goal-starter/create',
      component: withCognentAIGuard(GoalLibraryCreate),
    },
    {
      path: '/it-starter-program',
      component: withCognentAIGuard(StarterProgramList),
    },
    {
      path: '/it-organization',
      component: withCognentAIGuard(OrganizationList),
    },
    { path: '/it-admin', component: Admin },
    {
      path: '/it-dashboard/plan-selection',
      component: PlanSelection,
    },
    {
      path: '/it-dashboard/manage-billing',
      component: ManageBilling,
    },
    {
      path: '/it-dashboard/payment-details',
      component: PaymentDetails,
    },
    // {
    //   path: '/purchase-token-pack/:organizationId',
    //   component: PurchaseTokenPack,
    // },
    {
      path: '/payment-success',
      component: PaymentSuccessful,
    },
    {
      path: '/payment-failed',
      component: PaymentFailed,
    },
  ]

  return routes.map((r, i) => (
    <Route
      key={i}
      exact
      component={r.component}
      path={`${process.env.PUBLIC_URL}${r.path}`}
    />
  ))
}

const EducatorRoutes = () => {
  const routes = [
    { path: '/educator-dashboard', component: Dashboard },
    { path: '/educator-programs', component: Programs },
    { path: '/educator-library', component: Library },
    { path: '/educator-tasks', component: Tasks },
    { path: '/educator-teams', component: Teams },
    { path: '/educator-program-builder', component: ProgramBuilder },
    { path: '/educator-program-builder/:programId', component: ProgramBuilder },
    { path: '/educator-progress-tracker', component: ProgressTrackers },
    {
      path: '/educator-progress-tracker/:programId',
      component: ProgressTracker,
    },
  ]

  return routes.map((r, i) => (
    <Route
      key={i}
      exact
      component={r.component}
      path={`${process.env.PUBLIC_URL}${r.path}`}
    />
  ))
}

const ManagerRoutes = () => {
  const routes = [
    { path: '/manager-dashboard', component: Dashboard },
    { path: '/manager-programs', component: Programs },
    { path: '/manager-library', component: Library },
    { path: '/manager-tasks', component: Tasks },
    { path: '/manager-teams', component: Teams },
    { path: '/manager-program-builder', component: ProgramBuilder },
    { path: '/manager-program-builder/:programId', component: ProgramBuilder },
    { path: '/manager-progress-tracker', component: ProgressTrackers },
    {
      path: '/manager-progress-tracker/:programId',
      component: ProgressTracker,
    },
  ]

  return routes.map((r, i) => (
    <Route
      key={i}
      exact
      component={r.component}
      path={`${process.env.PUBLIC_URL}${r.path}`}
    />
  ))
}

const EmployeeRoutes = () => {
  const routes = [
    { path: '/employee-dashboard', component: EmployeeDashboard },
    { path: '/employee-programs', component: Programs },
    { path: '/employee-library', component: Library },
    { path: '/employee-goal-library', component: GoalLibrary },
    {
      path: '/employee-program/:programId/:programInstanceId',
      component: EmployeeTask,
    },
    {
      path: '/members-profile/:userId/:employeeId',
      component: EmployeeProfile,
    },
    { path: '/personal-hub', component: PersonalHub },

    { path: '/goals', component: PersonalGoals },
    { path: '/goals/create', component: PathwayForm },
    { path: '/goals/upload', component: UploadGoal },
    { path: '/personal-settings', component: PersonalSettings },
    { path: '/discoverable-orgs', component: DiscoverableOrg },
  ]

  return routes.map((r, i) => (
    <Route
      key={i}
      exact
      component={r.component}
      path={`${process.env.PUBLIC_URL}${r.path}`}
    />
  ))
}

export default function MainIndex() {
  const { userInfo, setUserInfo } = useContext(AuthContext)
  const roleName = userInfo?.employeeDetails?.role?.name
  const history = useHistory()
  const pathname = history?.location?.pathname?.split('-')[0]?.split('/')[1]
  const [tourOpen, setTourOpen] = useState(false)

  const {
    isDisabledWorkspace,
    isExceedingPlanWorkspace,
    isPowerfulTourOpen,
    setIsPowerfulTourOpen,
  } = useContext(NotificationContext)

  useEffect(() => {
    if (userInfo?.isFirstLogin && isMobile) {
      setTourOpen(true)
    }
  }, [userInfo?.isFirstLogin])

  useEffect(() => {
    const isAuthorizedRole = Roles[pathname]

    if (!isAuthorizedRole || !userInfo) return
    const userRole = userInfo?.employeeDetails?.role?.name

    if (userRole !== isAuthorizedRole) {
      // toast.error(
      //   t(
      //     'notification.authorizedError',
      //     `You don't have the permission to access this resource`,
      //   ),
      // )
      history.push(`/${userRole.toLowerCase()}-dashboard`)
    }
  }, [userInfo])

  if (!roleName) return null

  return (
    <div
      className={`main ${!isDisabledWorkspace && !isExceedingPlanWorkspace ? 'px-lg-4 px-md-4 pb-4' : ''}`}
    >
      <PlanWorkspaceWrapper>
        <Header />

        <div className="body d-flex pt-3">
          <GeneralRoutes roleName={roleName} />
          <HrRoutes />
          <ItRoutes />
          <EducatorRoutes />
          <ManagerRoutes />
          <EmployeeRoutes />
        </div>

        {isMobile && (
          <div
            className="position-fixed bottom-0 "
            style={{ right: 10, zIndex: 999 }}
          >
            <CaiChatProvider>
              <CaiPopup showInput={false} />
            </CaiChatProvider>
          </div>
        )}

        {tourOpen && (
          <MobileOnboarding
            onSessionEnd={() => {
              if (userInfo?.isFirstLogin) {
                localStorage.setItem('isCaiTourOpen', false)
              }
              setUserInfo((s) => ({ ...s, isFirstLogin: false }))
            }}
          />
        )}

        <PowerfulTour
          open={isPowerfulTourOpen}
          setOpen={setIsPowerfulTourOpen}
        />
      </PlanWorkspaceWrapper>
    </div>
  )
}
